import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import remarkGfm from 'remark-gfm';
import { FaFileCsv } from 'react-icons/fa';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  LineElement,
  PointElement,
  BubbleController,
  ScatterController,
  Filler,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Pie as ChartPie, Bar as ChartBar, Line as ChartLine, Radar as ChartRadar, Doughnut as ChartDoughnut, PolarArea as ChartPolarArea, Bubble as ChartBubble, Scatter as ChartScatter } from 'react-chartjs-2';
import { CHART_COLORS } from '../constants/colors';

ChartJS.register(
  ArcElement, CategoryScale, LinearScale, RadialLinearScale, BarElement,
  LineElement, PointElement, BubbleController, ScatterController,
  Filler, ChartTooltip, ChartLegend
);

function parseChartData(text) {
  const parseVal = str => parseFloat((str || '').replace(/[^0-9.]/g, '')) || 0;
  const hasPct   = str => (str || '').includes('%');

  const lines = text.split('\n');
  const tableLines = lines.filter(l => l.trim().startsWith('|') && l.includes('|'));

  if (tableLines.length >= 3) {
    const headers = tableLines[0].split('|').map(h => h.trim()).filter(Boolean);
    const rows = tableLines.slice(2)
      .map(l => l.split('|').map(c => c.trim()).filter(Boolean))
      .filter(r => r.length >= 2);

    const numericCols = [];
    for (let i = 1; i < headers.length; i++) {
      if (rows.some(r => r[i] && /\d/.test(r[i]))) numericCols.push(i);
    }
    if (numericCols.length === 0 || rows.length < 2) return null;

    const labels = rows.map(r => r[0]);

    if (numericCols.length >= 2) {
      const h = numericCols.map(c => headers[c] || '');

      if (numericCols.length >= 3 && /^x$/i.test(h[0]) && /^y$/i.test(h[1])) {
        const bubbleData = rows.map(r => ({
          label: r[0], x: parseVal(r[numericCols[0]]), y: parseVal(r[numericCols[1]]),
          r: Math.max(5, parseVal(r[numericCols[2]]) / 500), rawR: r[numericCols[2]],
        }));
        return { type: 'bubble', data: bubbleData, title: `${h[0]} vs ${h[1]}` };
      }

      if (numericCols.length === 2 && /^x$/i.test(h[0]) && /^y$/i.test(h[1])) {
        const scatterData = rows.map(r => ({ label: r[0], x: parseVal(r[numericCols[0]]), y: parseVal(r[numericCols[1]]) }));
        return { type: 'scatter', data: scatterData, title: `${headers[0]}: ${h[0]} vs ${h[1]}` };
      }

      if (numericCols.length === 2) {
        const col0pct = rows.every(r => hasPct(r[numericCols[0]]));
        const col1pct = rows.every(r => hasPct(r[numericCols[1]]));
        if (col0pct !== col1pct) {
          const barCol = col0pct ? numericCols[1] : numericCols[0];
          const lineCol = col0pct ? numericCols[0] : numericCols[1];
          return {
            type: 'mixed', labels,
            barData: rows.map(r => parseVal(r[barCol])), lineData: rows.map(r => parseVal(r[lineCol])),
            barLabel: headers[barCol], lineLabel: headers[lineCol],
            barRaw: rows.map(r => r[barCol] || '0'), lineRaw: rows.map(r => r[lineCol] || '0'),
            title: `${headers[barCol]} vs ${headers[lineCol]}`,
          };
        }
      }

      const datasets = numericCols.map(col => ({ label: headers[col], data: rows.map(r => parseVal(r[col])), raw: rows.map(r => r[col] || '0') }));
      return { type: 'stacked-bar', labels, datasets, title: `${headers[0]} breakdown` };
    }

    const valueCol = numericCols[0];
    const chartData = rows.map(r => ({ label: r[0], value: parseVal(r[valueCol]), raw: r[valueCol], isPct: hasPct(r[valueCol]) })).filter(d => d.value > 0);

    if (chartData.length >= 2) {
      const title = `${headers[0]} vs ${headers[valueCol]}`;
      const valueHeader = headers[valueCol] || '';
      const allPct = chartData.every(d => d.isPct);
      const sum = chartData.reduce((a, d) => a + d.value, 0);

      if (/doughnut|donut/i.test(valueHeader))                                            return { type: 'doughnut', data: chartData, title };
      if (/polar/i.test(valueHeader))                                                     return { type: 'polar',    data: chartData, title };
      if (/score|rating|rank|performance|index|skill|ability|metric/i.test(valueHeader)) return { type: 'radar',    data: chartData, title };
      const looksLikePie = allPct || /percent|pct|share|proportion|breakdown|%/i.test(valueHeader) || (sum >= 90 && sum <= 110);
      if (looksLikePie)                                                                    return { type: 'pie',      data: chartData, title };
      if (/^(month|year|week|quarter|period)$/i.test(headers[0]))                          return { type: 'line',     data: chartData, title };
      return { type: 'bar', data: chartData, title };
    }
  }

  const bulletRegex = /[-*•]\s*([^:\n]+):\s*\$?([\d,]+\.?\d*)\s*%?/g;
  let match;
  const listData = [];
  while ((match = bulletRegex.exec(text)) !== null) {
    const value = parseFloat(match[2].replace(/,/g, ''));
    if (!isNaN(value) && value > 0)
      listData.push({ label: match[1].trim(), value, raw: match[2], isPct: match[0].includes('%') });
  }
  if (listData.length >= 3) {
    const sum = listData.reduce((a, d) => a + d.value, 0);
    return { type: (listData.every(d => d.isPct) || (sum >= 95 && sum <= 105)) ? 'pie' : 'bar', data: listData, title: 'Breakdown' };
  }

  return null;
}

const RechartsPie = ({ data, title }) => {
  const chartData = { labels: data.map(d => d.label), datasets: [{ data: data.map(d => d.value), backgroundColor: data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]), borderColor: 'rgba(0,0,0,0.15)', borderWidth: 1 }] };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: '#999', font: { size: 11 }, padding: 8, boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${data[ctx.dataIndex].raw}` } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 200, position: 'relative' }}><ChartPie data={chartData} options={options} /></div></div>;
};

const RechartsBar = ({ data, title }) => {
  const chartData = { labels: data.map(d => d.label), datasets: [{ data: data.map(d => d.value), backgroundColor: data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]), borderRadius: 4 }] };
  const options = { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${data[ctx.dataIndex].raw}` } } }, scales: { x: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } }, y: { ticks: { color: '#bbb', font: { size: 11 } }, grid: { display: false } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: Math.max(120, data.length * 36), position: 'relative' }}><ChartBar data={chartData} options={options} /></div></div>;
};

const RechartsLine = ({ data, title }) => {
  const chartData = { labels: data.map(d => d.label), datasets: [{ data: data.map(d => d.value), borderColor: CHART_COLORS[0], backgroundColor: `${CHART_COLORS[0]}22`, borderWidth: 2, pointRadius: 4, pointBackgroundColor: CHART_COLORS[0], tension: 0.3, fill: true }] };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${data[ctx.dataIndex].raw || ctx.raw}` } } }, scales: { x: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } }, y: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 160, position: 'relative' }}><ChartLine data={chartData} options={options} /></div></div>;
};

const RechartsDoughnut = ({ data, title }) => {
  const chartData = { labels: data.map(d => d.label), datasets: [{ data: data.map(d => d.value), backgroundColor: data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]), borderColor: 'rgba(0,0,0,0.15)', borderWidth: 1 }] };
  const options = { responsive: true, maintainAspectRatio: false, cutout: '55%', plugins: { legend: { position: 'bottom', labels: { color: '#999', font: { size: 11 }, padding: 8, boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${data[ctx.dataIndex].raw}` } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 200, position: 'relative' }}><ChartDoughnut data={chartData} options={options} /></div></div>;
};

const RechartsPolar = ({ data, title }) => {
  const chartData = { labels: data.map(d => d.label), datasets: [{ data: data.map(d => d.value), backgroundColor: data.map((_, i) => `${CHART_COLORS[i % CHART_COLORS.length]}99`), borderColor: data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]), borderWidth: 1 }] };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: '#999', font: { size: 11 }, padding: 8, boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${data[ctx.dataIndex].raw}` } } }, scales: { r: { ticks: { color: '#999', font: { size: 9 }, backdropColor: 'transparent' }, grid: { color: 'rgba(128,128,128,0.2)' } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 220, position: 'relative' }}><ChartPolarArea data={chartData} options={options} /></div></div>;
};

const RechartsRadar = ({ data, title }) => {
  const chartData = { labels: data.map(d => d.label), datasets: [{ label: title || 'Score', data: data.map(d => d.value), backgroundColor: `${CHART_COLORS[0]}33`, borderColor: CHART_COLORS[0], borderWidth: 2, pointBackgroundColor: CHART_COLORS[0], pointRadius: 4 }] };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${data[ctx.dataIndex].raw}` } } }, scales: { r: { ticks: { color: '#999', font: { size: 9 }, backdropColor: 'transparent', stepSize: 20 }, grid: { color: 'rgba(128,128,128,0.2)' }, pointLabels: { color: '#bbb', font: { size: 11 } }, suggestedMin: 0, suggestedMax: 100 } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 240, position: 'relative' }}><ChartRadar data={chartData} options={options} /></div></div>;
};

const RechartsStackedBar = ({ labels, datasets, title }) => {
  const chartData = { labels, datasets: datasets.map((ds, i) => ({ label: ds.label, data: ds.data, backgroundColor: CHART_COLORS[i % CHART_COLORS.length], borderRadius: i === datasets.length - 1 ? 4 : 0 })) };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: '#999', font: { size: 11 }, padding: 8, boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.dataset.label}: ${datasets[ctx.datasetIndex].raw[ctx.dataIndex]}` } } }, scales: { x: { stacked: true, ticks: { color: '#999', font: { size: 10 } }, grid: { display: false } }, y: { stacked: true, ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 220, position: 'relative' }}><ChartBar data={chartData} options={options} /></div></div>;
};

const RechartsMixed = ({ labels, barData, lineData, barLabel, lineLabel, title, barRaw, lineRaw }) => {
  const chartData = { labels, datasets: [{ type: 'bar', label: barLabel, data: barData, backgroundColor: `${CHART_COLORS[0]}bb`, borderRadius: 4, yAxisID: 'y' }, { type: 'line', label: lineLabel, data: lineData, borderColor: CHART_COLORS[1], backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: CHART_COLORS[1], tension: 0.3, yAxisID: 'y1' }] };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: '#999', font: { size: 11 }, padding: 8, boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => ctx.datasetIndex === 0 ? ` ${barLabel}: ${barRaw[ctx.dataIndex]}` : ` ${lineLabel}: ${lineRaw[ctx.dataIndex]}` } } }, scales: { x: { ticks: { color: '#999', font: { size: 10 } }, grid: { display: false } }, y: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } }, y1: { position: 'right', ticks: { color: CHART_COLORS[1], font: { size: 10 }, callback: v => `${v}%` }, grid: { display: false } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 220, position: 'relative' }}><ChartBar data={chartData} options={options} /></div></div>;
};

const RechartsScatter = ({ data, title }) => {
  const chartData = { datasets: [{ label: title || 'Data', data: data.map(d => ({ x: d.x, y: d.y })), backgroundColor: `${CHART_COLORS[0]}bb`, pointRadius: 6, pointHoverRadius: 8 }] };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${data[ctx.dataIndex].label}: (${data[ctx.dataIndex].x}, ${data[ctx.dataIndex].y})` } } }, scales: { x: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } }, y: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 220, position: 'relative' }}><ChartScatter data={chartData} options={options} /></div></div>;
};

const RechartsBubble = ({ data, title }) => {
  const chartData = { datasets: data.map((d, i) => ({ label: d.label, data: [{ x: d.x, y: d.y, r: d.r }], backgroundColor: `${CHART_COLORS[i % CHART_COLORS.length]}99`, borderColor: CHART_COLORS[i % CHART_COLORS.length], borderWidth: 1 })) };
  const options = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { color: '#999', font: { size: 11 }, padding: 8, boxWidth: 12 } }, tooltip: { callbacks: { label: (ctx) => { const d = data[ctx.datasetIndex]; return ` ${d.label}: x=${d.x}, y=${d.y}, size=${d.rawR}`; } } } }, scales: { x: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } }, y: { ticks: { color: '#999', font: { size: 10 } }, grid: { color: 'rgba(128,128,128,0.15)' } } } };
  return <div className="rchart-container">{title && <div className="rchart-title">{title}</div>}<div style={{ height: 240, position: 'relative' }}><ChartBubble data={chartData} options={options} /></div></div>;
};

function parseMarkdownTable(text) {
  const lines = text.split('\n');
  const tableLines = lines.filter(l => l.trim().startsWith('|') && l.includes('|'));
  if (tableLines.length < 3) return null;
  const headers = tableLines[0].split('|').map(h => h.trim()).filter(Boolean);
  const rows = tableLines.slice(2).map(l => l.split('|').map(c => c.trim()).filter(Boolean)).filter(r => r.length >= 1);
  if (headers.length < 1 || rows.length < 1) return null;
  return { headers, rows };
}

function extractMapBlock(text) {
  const match = text.match(/```map\s*([\s\S]*?)```/);
  if (!match) return null;
  try { return { data: JSON.parse(match[1].trim()), fullMatch: match[0] }; }
  catch { return null; }
}

const HeatmapLayer = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !points?.length) return;
    const heatPoints = points.map(p => [p.lat, p.lng, p.intensity ?? 0.5]);
    const heat = L.heatLayer(heatPoints, { radius: 35, blur: 25, maxZoom: 17, max: 1.0, gradient: { 0.2: '#6A55F7', 0.5: '#f59e0b', 0.8: '#ef4444' } }).addTo(map);
    return () => { try { map.removeLayer(heat); } catch {} };
  }, [map, points]);
  return null;
};

const createChatMarkerIcon = () => new L.DivIcon({
  className: '',
  html: `<svg viewBox="0 0 24 36" width="20" height="30" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C7.6 0 4 3.6 4 8c0 6 8 16 8 16s8-10 8-16c0-4.4-3.6-8-8-8z" fill="#6A55F7" stroke="white" stroke-width="1.5"/><circle cx="12" cy="8" r="3" fill="white"/></svg>`,
  iconSize: [20, 30], iconAnchor: [10, 30], popupAnchor: [0, -30],
});

function MapCenterUpdater({ center }) {
  const map = useMap();
  useEffect(() => { map.setView(center, 14); }, [center, map]);
  return null;
}

const ChatMap = ({ mapData }) => {
  if (!mapData?.points?.length) return null;
  const center = mapData.center || [43.6532, -79.3832];
  const zoom = mapData.zoom || 12;
  return (
    <div className="rchart-container">
      {mapData.title && <div className="rchart-title">{mapData.title}</div>}
      <div style={{ height: 260, borderRadius: 8, overflow: 'hidden', marginTop: 6 }}>
        <MapContainer center={center} zoom={zoom} style={{ width: '100%', height: '100%' }} scrollWheelZoom={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
          <MapCenterUpdater center={center} />
          {mapData.type === 'heatmap' && <HeatmapLayer points={mapData.points} />}
          {mapData.points.map((p, i) => mapData.type === 'markers' && (
            <Marker key={i} position={[p.lat, p.lng]} icon={createChatMarkerIcon()}>
              {(p.label || p.value) && <Popup><div style={{ fontSize: 13, lineHeight: 1.5 }}>{p.label && <strong>{p.label}</strong>}{p.value && <div style={{ color: '#555', marginTop: 2 }}>{p.value}</div>}</div></Popup>}
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};

function triggerCSVDownload(headers, rows) {
  const escape = val => `"${String(val).replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `chatbot-export-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function stripTableLines(text) {
  return text.split('\n').filter(l => !l.trim().startsWith('|')).join('\n').trim();
}

const ChartWrapper = ({ text }) => {
  const [showTable, setShowTable] = useState(false);
  const mapBlock  = extractMapBlock(text);
  const cleanText = mapBlock ? text.replace(mapBlock.fullMatch, '').trim() : text;
  const chartData = !mapBlock ? parseChartData(text) : null;
  const tableData = parseMarkdownTable(cleanText);
  const displayText = (chartData && !showTable) ? stripTableLines(cleanText) : cleanText;

  const csvBtn = tableData ? (
    <button
      type="button"
      className="chart-export-btn"
      onClick={() => triggerCSVDownload(tableData.headers, tableData.rows)}
      title="Download as CSV"
      aria-label="Download as CSV"
    >
      <FaFileCsv size={18} />
    </button>
  ) : null;

  const chartEl = (
    <>
      {chartData && chartData.type === 'pie'         && <RechartsPie         data={chartData.data}                                                             title={chartData.title} />}
      {chartData && chartData.type === 'doughnut'    && <RechartsDoughnut    data={chartData.data}                                                             title={chartData.title} />}
      {chartData && chartData.type === 'polar'       && <RechartsPolar       data={chartData.data}                                                             title={chartData.title} />}
      {chartData && chartData.type === 'radar'       && <RechartsRadar       data={chartData.data}                                                             title={chartData.title} />}
      {chartData && chartData.type === 'line'        && <RechartsLine        data={chartData.data}                                                             title={chartData.title} />}
      {chartData && chartData.type === 'scatter'     && <RechartsScatter     data={chartData.data}                                                             title={chartData.title} />}
      {chartData && chartData.type === 'bubble'      && <RechartsBubble      data={chartData.data}                                                             title={chartData.title} />}
      {chartData && chartData.type === 'stacked-bar' && <RechartsStackedBar  labels={chartData.labels} datasets={chartData.datasets}                          title={chartData.title} />}
      {chartData && chartData.type === 'mixed'       && <RechartsMixed       labels={chartData.labels} barData={chartData.barData} lineData={chartData.lineData} barLabel={chartData.barLabel} lineLabel={chartData.lineLabel} barRaw={chartData.barRaw} lineRaw={chartData.lineRaw} title={chartData.title} />}
      {chartData && (chartData.type === 'bar' || !chartData.type) && <RechartsBar data={chartData.data} title={chartData.title} />}
    </>
  );

  return (
    <div>
      {mapBlock && <ChatMap mapData={mapBlock.data} />}
      {chartData ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <button
              onClick={() => setShowTable(false)}
              style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1px solid',
                cursor: 'pointer',
                background: !showTable ? 'var(--accent, #1976d2)' : 'transparent',
                color: !showTable ? '#fff' : 'var(--text-light, #888)',
                borderColor: !showTable ? 'var(--accent, #1976d2)' : 'var(--border-color, #ddd)',
              }}
            >Chart</button>
            <button
              onClick={() => setShowTable(true)}
              style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 10, border: '1px solid',
                cursor: 'pointer',
                background: showTable ? 'var(--accent, #1976d2)' : 'transparent',
                color: showTable ? '#fff' : 'var(--text-light, #888)',
                borderColor: showTable ? 'var(--accent, #1976d2)' : 'var(--border-color, #ddd)',
              }}
            >Table</button>
            {csvBtn}
          </div>
          {!showTable && <div className="chart-with-export-body">{chartEl}</div>}
        </>
      ) : null}
      {!chartData && csvBtn}
      {displayText && <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayText}</ReactMarkdown>}
    </div>
  );
};

export { parseChartData };
export default ChartWrapper;
